import { makeStyles } from '@material-ui/styles'
import { take, takeRight } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useResizeDetector } from 'react-resize-detector/build/withPolyfill'
import { countWhile } from 'utils/arrayUtils'
import { isMobile } from 'utils/mobileUtils'
import { compareTags } from 'utils/tagUtils'
import GrinChip from '../GrinChip'
import GrinMenu from '../menu/GrinMenu'
import GrinMenuItem from '../menu/GrinMenuItem'
import GrinTag from './GrinTag'
import TagsPicker from './TagsPicker'

const TagsMargin = 10
const MoreTagsPlaceholder = 70

const useStyles = makeStyles(({ isMobile }) => ({
  root: {
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    position: 'relative'
  },
  allTagsHiddenForWidthCalculation: {
    position: 'absolute',
    top: 0,
    left: 0,
    visibility: 'hidden'
  },
  visibleTag: {
    marginRight: TagsMargin
  },
  showMoreChip: {
    color: 'var(--text-color-16)',
    cursor: 'pointer'
  },
  tag: {
    borderRadius: 30,
    height: ({ isMobile }) => (isMobile ? 30 : 35),
    padding: ({ isMobile }) => (isMobile ? 2 : 5),
    backgroundColor: '#F0F0F0'
  }
}))

const FilterTagsList = ({
  tags = [],
  className,
  showIcon = true,
  canDeleteTags = true,
  onDeleteTag,
  observeResize = true
}) => {
  const classes = useStyles({ isMobile: isMobile() })
  const { t } = useTranslation()
  const { width, ref } = useResizeDetector()

  const [displayedTags, setDisplayedTags] = useState([])
  const [hiddenTags, setHiddenTags] = useState([])
  const [isHiddenTagsDropdownOpen, setIsHiddenTagsDropdownOpen] = useState(false)
  const [tagsWidth, setTagsWidth] = useState([])

  const sortedTags = useMemo(() => tags.sort(compareTags), [tags])

  const calculateAllTagsWidth = useCallback(() => {
    if (!ref.current) return []
    const hiddenTagsElements = ref.current.querySelectorAll(`.${classes.allTagsHiddenForWidthCalculation} > div > *`)

    const allTagsWidth = Array.from(hiddenTagsElements).map(tag => tag.offsetWidth + TagsMargin)
    return allTagsWidth
  }, [ref, classes.allTagsHiddenForWidthCalculation])

  const countTagsToDisplay = useCallback((width, allTagsWidth) => {
    const fitInTagsCount = countWhile(allTagsWidth, nextWidth => nextWidth < width - MoreTagsPlaceholder)
    return fitInTagsCount
  }, [])

  useEffect(() => {
    if (!width) {
      return
    }
    const allTagsWidth = calculateAllTagsWidth()
    const tagsCountToDisplay = countTagsToDisplay(width, allTagsWidth)

    const updatedDisplayedTags = take(sortedTags, tagsCountToDisplay)
    const updatedHiddenTags = takeRight(sortedTags, sortedTags.length - tagsCountToDisplay)
    setDisplayedTags(updatedDisplayedTags)
    setHiddenTags(updatedHiddenTags)
  }, [sortedTags, calculateAllTagsWidth, width, countTagsToDisplay])

  const handleDelete = useCallback(
    tag => {
      const deletedTagIndex = sortedTags.findIndex(sortedTag => sortedTag.value === tag.value)
      const oldTagsWidth = [...tagsWidth]
      oldTagsWidth.splice(deletedTagIndex, 1)
      onDeleteTag(tag)
      setTagsWidth(oldTagsWidth)
    },
    [onDeleteTag, sortedTags, tagsWidth]
  )

  const hiddenTagsAmount = useMemo(() => hiddenTags.length, [hiddenTags])

  return (
    <div className={[classes.root, className].join(' ')} ref={ref}>
      {displayedTags.map(tag => (
        <GrinTag
          key={tag.value}
          tag={tag}
          className={[classes.tag, classes.visibleTag, 'tag-visible'].join(' ')}
          canDeleteSystem={canDeleteTags}
          canDeleteCustom={canDeleteTags}
          canDeleteStatus={canDeleteTags}
          onDelete={() => handleDelete(tag)}
        />
      ))}
      {hiddenTagsAmount > 0 && (
        <>
          <GrinMenu
            transformOrigin={{ vertical: -30, horizontal: 'left' }}
            disableHoverEffects
            triggerComponent={
              <div>
                <GrinChip
                  text={t('pages.patients.selectedPatient.tags.showMore', { amount: hiddenTags.length })}
                  className={classes.showMoreChip}
                />
              </div>
            }
            open={isHiddenTagsDropdownOpen}
            onClose={() => setIsHiddenTagsDropdownOpen(false)}
            onOpen={() => setIsHiddenTagsDropdownOpen(true)}
          >
            {hiddenTags.map(tag => (
              <div key={tag.value}>
                <GrinMenuItem nonActionable>
                  <GrinTag
                    key={tag.value}
                    tag={tag}
                    className={classes.tag}
                    canDeleteSystem={canDeleteTags}
                    canDeleteCustom={canDeleteTags}
                    canDeleteStatus={canDeleteTags}
                    onDelete={() => handleDelete(tag)}
                  />
                </GrinMenuItem>
              </div>
            ))}
          </GrinMenu>
        </>
      )}
      <TagsPicker showIcon={showIcon} />
      <div className={classes.allTagsHiddenForWidthCalculation}>
        {sortedTags.map(tag => (
          <GrinTag
            tag={tag}
            className={classes.tag}
            canDeleteSystem={canDeleteTags}
            canDeleteCustom={canDeleteTags}
            canDeleteStatus={canDeleteTags}
            onDelete={() => handleDelete(tag)}
          />
        ))}
      </div>
    </div>
  )
}

export default React.memo(FilterTagsList)
